import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { BcRestApi } from 'common/api/rest-api';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { AgentLoads } from 'common/api/models/agent-loads.model';
import { PCacheable } from 'ngx-cacheable';
import { CoolLocalStorage } from '@angular-cool/storage';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { AgentCapacities } from 'common/api/models/agent-capacities.model';
import debounce from 'debounce-promise';
import { Sort } from 'components/queue-view/live-workload-tab/models/sort';
import { PollingService } from 'shared/services/polling-service/polling.service';

@Injectable({
  providedIn: 'root'
})
export class AgentListTabService {
  private static readonly CACHE_BUSTER = new Subject<void>();
  public static readonly LOAD_REFRESH_INTERVAL = 60000; // ms
  public static readonly CAPACITY_DEBOUNCE_TIME = 1000; // ms

  private readonly _tabName = 'live-workload-agents';

  constructor(private _bcRestApi: BcRestApi,
              private _localStorageService: CoolLocalStorage,
              private _pollingService: PollingService) {
  }

  public static evictCache(): void {
    this.CACHE_BUSTER.next();
  }

  public observeLoad(departmentIds: string[]): Observable<AgentLoads> {
    return this._pollingService.poll(() => this._getLoad(departmentIds), AgentListTabService.LOAD_REFRESH_INTERVAL);
  }

  public getColumnSortingSettings(): Sort {
    const savedSortingSetting = this._localStorageService.getItem(bcSessionKeys.storage.queueViewColumnSort + this._tabName);
    return savedSortingSetting ? JSON.parse(savedSortingSetting) : null;
  }

  public setColumnSortingSettings(activeSort): void {
    this._localStorageService.setItem(bcSessionKeys.storage.queueViewColumnSort + this._tabName, JSON.stringify(activeSort));
  }

  public convertServiceTypeToLoadType(serviceType: string): string {
    switch (serviceType) {
      case WorkItemTypes.CHAT:
        return 'chat';
      case WorkItemTypes.EMAIL:
        return 'email';
      case WorkItemTypes.SMS:
        return 'sms';
      case WorkItemTypes.FACEBOOK:
        return 'facebook';
      case WorkItemTypes.ASYNC_MESSAGE:
        return 'async';
      case WorkItemTypes.OMNI:
        return 'omni';
      default:
        return null;
    }
  }

  public getCapacitiesByOperatorId(operatorId: string): Promise<AgentCapacities> {
    return this._debouncedGetCapacitiesByOperatorId(operatorId);
  }

  public getCapacitiesByDepartmentIds(departmentIds: string[]): Promise<AgentCapacities> {
    return this._bcRestApi.getInitialCapacity(departmentIds);
  }

  private _debouncedGetCapacitiesByOperatorId = debounce(argsList => {
    const operatorIds = argsList.reduce((set, args) => set.add(args[0]), new Set<string>());
    const capacityPromise = this._bcRestApi.getCapacity([...operatorIds.values()]);
    return argsList.map(() => capacityPromise);
  }, AgentListTabService.CAPACITY_DEBOUNCE_TIME, { accumulate: true });

  @PCacheable({
    maxAge: 1000,
    cacheBusterObserver: AgentListTabService.CACHE_BUSTER.asObservable()
  })
  private _getLoad(departmentIds: string[]): Promise<AgentLoads> {
    return this._bcRestApi.getAgentLoads(departmentIds);
  }
}
